<script setup lang="ts">
// The panel's state in a slot of fixed height, there whether or not
// anything is wrong: the first line the state (the default slot), the
// second the message cut to one line (whole on hover). A warning that came
// and went used to push the whole panel down under the pointer (Niels:
// misclicks); now the slot only changes colour. Clicking it opens the whole
// message, and what to do about it (the `more` slot), over the panel
// instead of in it. `idle`: the second line with no message ("no problems"
// unless said: the machine's "2 acknowledged, on Health").
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'

const props = defineProps<{
    tone: 'ok' | 'warn' | 'bad' | 'note'
    label?: string
    message?: string
    idle?: string
}>()
const open = ref(false)
const can = computed(() => !!props.message)
const root = useTemplateRef<HTMLElement>('root')
const border = {
    ok: 'border-line',
    note: 'border-line',
    warn: 'border-warn/45 bg-warn/6',
    bad: 'border-bad/50 bg-bad/6',
}
const text = { ok: 'text-fg-3', note: 'text-fg-2', warn: 'text-warn', bad: 'text-bad' }
function outside(e: MouseEvent) {
    if (open.value && !root.value?.contains(e.target as Node)) open.value = false
}
function onKey(e: KeyboardEvent) {
    if (open.value && e.key === 'Escape') {
        e.stopPropagation() // a drawer or the panel under it stays as it is
        open.value = false
    }
}
onMounted(() => {
    document.addEventListener('mousedown', outside)
    window.addEventListener('keydown', onKey, true)
})
onUnmounted(() => {
    document.removeEventListener('mousedown', outside)
    window.removeEventListener('keydown', onKey, true)
})
</script>

<template>
    <div ref="root" class="relative">
        <button
            type="button"
            class="block h-12 w-full rounded-md px-2.5 py-1.5 text-left text-xs hover:bg-bg-2"
            :class="[border[tone], { 'cursor-default hover:bg-transparent': !can }]"
            :aria-expanded="can ? open : undefined"
            :title="props.message"
            @click="can && (open = !open)"
        >
            <span class="flex items-baseline gap-1.5"
                ><slot /><span class="min-w-0 flex-1" /><span v-if="can" class="text-[11px] text-fg-3"
                    >{{ label }} ▾</span
                ></span
            >
            <span class="block truncate font-mono" :class="text[tone]">{{
                props.message || props.idle || 'no problems'
            }}</span>
        </button>
        <div
            v-if="open && can"
            class="absolute inset-x-0 top-[calc(100%+4px)] z-20 max-h-80 popover overflow-auto p-2.5 text-xs"
            role="dialog"
            :aria-label="label"
        >
            <div class="mb-1 subhead">{{ label }}</div>
            <div
                v-if="props.message"
                class="font-mono break-words whitespace-pre-wrap"
                :class="text[tone]"
                v-text="props.message"
            />
            <slot name="more" />
        </div>
    </div>
</template>
